import React, { useEffect, memo, useState, useCallback } from 'react'
import { observer } from 'mobx-react'
import { NavigateFunction } from 'react-router'
import { Typography, Button } from '@mui/material'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { getButtonSizes } from 'components/FormNavigation/getButtonSizes'
import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import * as CSSTypes from 'csstype'
import useEmotionStyles from 'hooks/useEmotionStyles'
import LegalHintTexts from 'texts/common/copy/legal'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import {
  IBAN_PATH,
  usePayoutAccountValidation
} from 'hooks/usePayoutAccountValidation'
import {
  CONFIG_DDF_SMAVA,
  CONFIG_SECOND_DEBTOR_SMAVA,
  isRceEditConfig
} from 'config/utils/config'
import { useField } from 'react-final-form'
import FormNavigationTemplate from 'components/FormNavigation/FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { isPremiumCustomer } from 'utils/user'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useDacSkipButton } from 'hooks/useDacSkipButton'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
  isOfferLabel?: boolean
}

const styles = () => ({
  flexBasisGrid: {
    flexBasis: 'auto',
    marginTop: '5px'
  },
  button: {
    maxHeight: '48px'
  },
  buttonFullHeight: {
    height: '48px',
    '& button': {
      height: '48px'
    }
  },
  buttonTitle: {
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.5rem'
  },
  buttonHint: {
    color: '#fff',
    fontSize: '0.75rem',
    lineHeight: '1.25rem'
  },
  buttonTextContainer: {
    flexDirection: 'column' as CSSTypes.Property.FlexDirection,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
})

const FormNavigation = observer(
  ({ onBack, onNext, isOfferLabel, navigate }: Props) => {
    const {
      rootStore: {
        experimentData,
        setIsSkipDacCTAVisible,
        isDacShowOnCtaClick,
        isInIframe,
        urlParams: { backUrl },
        traversal: {
          data: {
            common: { amount },
            debtors: {
              primary: {
                personal: { birthday },
                income
              }
            }
          }
        }
      }
    } = useRootStore()

    const isPayoutAccountReady = usePayoutAccountValidation(true)
    const [
      isIbanPresentOnPayoutAccountPage,
      setIsIbanPresentOnPayoutAccountPage
    ] = useState(false)

    const {
      page: {
        isFirstDisplayedPage,
        getCurrentIndex,
        isLastDisplayedPage,
        config
      },
      navigation: { currentPageInSession, isDacPage },
      traversal: { data },
      traversalSession: { dacWidgetState }
    } = useStores()

    const isLastPage = isLastDisplayedPage(currentPageInSession)
    const debtorsAge = getAge(birthday)
    const ageGroup = useAgeGroup()
    const isPremium = isPremiumCustomer({ debtorsAge, amount, income })
    const isPremiumOrOldAge = isPremium || ageGroup === AgeGroup.OLD_AGE_GROUP

    // @ts-ignore - experimentData type needs refinement for dynamic access
    const isInSkipDacV4Experiment =
      // @ts-ignore - experimentData type needs refinement for dynamic access
      experimentData[Experiments.CAT_4478_DAC_SKIP_BUTTON_V4] ===
      PossibleVariants.VARIATION_B

    const ibanField = useField(IBAN_PATH, {})

    const { isSaveButtonVisible } = useSaveButton({
      onSuccess: () => {},
      onError: () => {}
    })

    const isYoungOrMiddleAgeGroup =
      ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP

    const isMiddleAgeGroup = ageGroup === AgeGroup.MIDDLE_AGE_GROUP

    const isRceConfig = isRceEditConfig(config.name)

    const { showDacSkipButton, skipButtonText } = useDacSkipButton({
      isDacPage,
      isDacShowOnCtaClick,
      isPremium,
      isPremiumOrOldAge,
      isYoungOrMiddleAgeGroup,
      ageGroup,
      dacWidgetState,
      experimentFlags: { isInSkipDacV4Experiment },
      isDacSkipButtonTriggersIgnored: isInSkipDacV4Experiment
        ? false
        : isMiddleAgeGroup && !isPremiumOrOldAge,
      fallbackButtonText: 'Ich habe keinen Online-Banking-Zugang',
      getTimeout: isInSkipDacV4Experiment
        ? ({ ageGroup: ageGroupParam, isPremium: isPremiumParam }) => {
            if (
              ageGroupParam === AgeGroup.OLD_AGE_GROUP ||
              (!isPremiumParam && ageGroupParam === AgeGroup.MIDDLE_AGE_GROUP)
            )
              return 20000
            if (isPremiumParam) return 25000
            if (ageGroupParam === AgeGroup.YOUNG_AGE_GROUP) return 60000
            return 25000
          }
        : undefined
    })

    const showMiddleAgeOrRceDacSkipButton = isInSkipDacV4Experiment
      ? false
      : (isMiddleAgeGroup &&
          isDacPage &&
          !isPremiumOrOldAge &&
          !isRceConfig &&
          config.name !== CONFIG_SECOND_DEBTOR_SMAVA) ||
        (isRceConfig && isDacPage)

    useEffect(() => {
      setIsIbanPresentOnPayoutAccountPage(
        currentPageInSession === 'payoutAccount' &&
          ibanField.input.value &&
          dacWidgetState === 'initial'
      )
    }, [data, currentPageInSession, dacWidgetState])

    const currentIndex = getCurrentIndex()

    const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
      trackInAmplitude(event, { origin: 'taurine', ...payload })
    }

    const onNextWithTracking = useCallback(() => {
      onNext()
    }, [onNext, data])

    useEffect(() => {
      if (dacWidgetState === 'success') {
        trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
        onNextWithTracking()
      }
    }, [dacWidgetState])

    useEffect(() => {
      if (showDacSkipButton) {
        setIsSkipDacCTAVisible(true)
      }
    }, [showDacSkipButton])

    const skipDacButtonOnClick = useCallback(() => {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_button_clicked',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          },
          payload: {
            text_on_button: skipButtonText
          }
        }
      })
      onNext()
    }, [onNext, skipButtonText])

    const { buttonTitle, buttonHint, buttonTextContainer } =
      useEmotionStyles(styles)

    const isFirstPage = isFirstDisplayedPage(currentPageInSession)

    const getSubmitLabel = () => {
      if (isFirstPage || isOfferLabel) {
        const title =
          isOfferLabel || ageGroup === AgeGroup.OLD_AGE_GROUP
            ? 'Weiter zu den Angeboten'
            : 'Kreditvergleich starten'

        const hint = 'kostenlos und unverbindlich'

        return (
          <div className={buttonTextContainer}>
            <Typography className={buttonTitle}>{title}</Typography>
            <Typography className={buttonHint}>{hint}</Typography>
          </div>
        )
      }

      return 'Weiter'
    }

    const backLabel = !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : ''

    const isBackButtonHidden = !backLabel || isFirstPage

    const getIsContinueButtonHidden = () => {
      if (isRceConfig) {
        return isLastPage && dacWidgetState !== 'success'
      }

      if (isLastPage && isPremiumOrOldAge && isDacPage) {
        return true
      }

      if (config.name === CONFIG_SECOND_DEBTOR_SMAVA) {
        return false
      }

      return (
        isLastPage &&
        isPayoutAccountReady &&
        dacWidgetState !== 'success' &&
        !isIbanPresentOnPayoutAccountPage &&
        isDacPage &&
        ageGroup !== AgeGroup.OLD_AGE_GROUP
      )
    }

    const showDacLegalText =
      isLastPage &&
      isPayoutAccountReady &&
      isDacPage &&
      config.name === CONFIG_DDF_SMAVA

    const isContinueButtonHidden = getIsContinueButtonHidden()

    const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
      getButtonSizes(
        isBackButtonHidden,
        isSaveButtonVisible,
        isContinueButtonHidden
      )

    const onBackHandler = () => {
      if (isLastPage) {
        broadcastToParent({
          eventName: RegisteredEventName.tracking,
          data: {
            event: 'back_button_click_on_dac_page',
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              ...getOptimizelyTrackingAttribute(data)
            }
          }
        })
      }
      onBack && onBack()
    }

    return (
      <FormNavigationTemplate
        onBack={onBackHandler}
        onNext={onNext}
        navigate={navigate}
        legalText={LegalHintTexts.smavaLegalHint}
        isContinueWithoutDacVisible={false}
        isBackButtonHidden={isBackButtonHidden}
        isContinueButtonHidden={isContinueButtonHidden}
        sizeOfContinueButton={sizeOfContinueButton}
        sizeOfBackButton={
          isRceConfig && (showDacSkipButton || showMiddleAgeOrRceDacSkipButton)
            ? 4
            : sizeOfBackButton
        }
        sizeOfSaveButton={
          isDacPage && (showMiddleAgeOrRceDacSkipButton || showDacSkipButton)
            ? 4
            : sizeOfSaveButton
        }
        submitLabel={getSubmitLabel()}
        backLabel={backLabel}
        sizeOfSkipDacButton={8}
        onNextWithTracking={onNextWithTracking}
        trackWithOrigin={trackWithOrigin}
        continueWithoutDacButtonVariant={'text'}
        skipDacButton={
          isDacPage &&
          (showMiddleAgeOrRceDacSkipButton || showDacSkipButton) ? (
            <Button
              id="test-skip-dac"
              name="test-skip-dac"
              onClick={skipDacButtonOnClick}
              color="primary"
              fullWidth
              variant={'outlined'}
              endIcon={isPremiumOrOldAge ? <ArrowForwardIcon /> : undefined}
              sx={{
                fontWeight: 500,
                fontSize: '15px',
                height: '50px'
              }}
            >
              {skipButtonText}
            </Button>
          ) : null
        }
        showDacLegalText={showDacLegalText}
      />
    )
  }
)

export default memo(FormNavigation)
